import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import PropTypes from "prop-types";

import {
  createRetailPOSTicketPresentation,
} from "./retailPOSTicketPresentation";


const DEFAULT_PDF_FORMAT =
  "A4";


export default function RetailPOSTicketWorkspace({
  runtime,
  context,
  receiptSnapshot,
}) {
  const [
    outputUrl,
    setOutputUrl,
  ] = useState(null);

  const [
    outputLoading,
    setOutputLoading,
  ] = useState(false);

  const [
    outputError,
    setOutputError,
  ] = useState(null);


  /*
   * Historical Workspace tests may still provide a flat
   * Receipt mock.
   *
   * The authoritative production contract remains:
   *
   * {
   *   company,
   *   receipt,
   *   lines,
   *   payments
   * }
   *
   * We do not relax retailPOSTicketPresentation for legacy
   * mocks. The UX simply does not project an invalid payload.
   */
  const canProject =
    Boolean(
      receiptSnapshot
      && typeof receiptSnapshot === "object"
      && receiptSnapshot.receipt
      && typeof receiptSnapshot.receipt
        === "object"
    );


  const presentation =
    useMemo(
      () => {
        if (!canProject) {
          return null;
        }

        return createRetailPOSTicketPresentation(
          receiptSnapshot
        );
      },
      [
        canProject,
        receiptSnapshot,
      ]
    );


  useEffect(
    () => {
      return () => {
        if (
          outputUrl
          && typeof URL?.revokeObjectURL
            === "function"
        ) {
          URL.revokeObjectURL(
            outputUrl
          );
        }
      };
    },
    [
      outputUrl,
    ]
  );


  async function resolvePdfOutput() {
    if (
      !presentation
      || outputLoading
    ) {
      return null;
    }

    setOutputLoading(true);
    setOutputError(null);

    try {
      const output =
        await runtime.printReceipt(
          presentation.receipt,
          {
            ...context,

            companyUuid:
              context?.companyUuid
              ?? presentation.identity
                .companyUuid,

            format:
              DEFAULT_PDF_FORMAT,
          }
        );

      const blob =
        normalizePdfBlob(
          output
        );

      if (
        outputUrl
        && typeof URL?.revokeObjectURL
          === "function"
      ) {
        URL.revokeObjectURL(
          outputUrl
        );
      }

      const url =
        URL.createObjectURL(
          blob
        );

      setOutputUrl(
        url
      );

      return url;
    } catch (error) {
      setOutputError(
        error?.message
        ?? "Impossible de préparer le ticket PDF."
      );

      return null;
    } finally {
      setOutputLoading(false);
    }
  }


  async function handlePreparePdf() {
    await resolvePdfOutput();
  }


  async function handleOpenForPrint() {
    const url =
      outputUrl
      ?? await resolvePdfOutput();

    if (!url) {
      return;
    }

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  }


  if (!presentation) {
    return (
      <section
        className="retail-pos-ticket"
        data-testid="retail-pos-ticket-workspace"
        aria-label="Ticket de vente"
      >
        <p
          role="status"
          data-testid="retail-pos-ticket-unavailable"
        >
          Projection du ticket indisponible.
        </p>
      </section>
    );
  }


  return (
    <section
      className="retail-pos-ticket"
      data-testid="retail-pos-ticket-workspace"
      aria-labelledby="retail-pos-ticket-title"
    >
      <header
        className="retail-pos-ticket__header"
      >
        <div>
          <p
            className="retail-pos-ticket__eyebrow"
          >
            Ticket de vente
          </p>

          <h3
            id="retail-pos-ticket-title"
          >
            {presentation.company?.name
              ?? "Point de vente"}
          </h3>
        </div>

        <div
          className="retail-pos-ticket__identity"
        >
          <strong>
            {presentation.identity
              .receiptNumber
              ?? presentation.identity
                .receiptUuid}
          </strong>

          {presentation.identity
            .issuedAt && (
            <span>
              {formatDate(
                presentation.identity
                  .issuedAt
              )}
            </span>
          )}
        </div>
      </header>


      <div
        className="retail-pos-ticket__lines"
        data-testid="retail-pos-ticket-lines"
      >
        {presentation.lines.length === 0
          ? (
            <p>
              Aucune ligne de vente.
            </p>
          )
          : presentation.lines.map(
              (line, index) => (
                <div
                  className="retail-pos-ticket__line"
                  key={
                    line.uuid
                    ?? `${line.name}-${index}`
                  }
                >
                  <div>
                    <strong>
                      {line.name
                        || "Article"}
                    </strong>

                    <span>
                      {formatQuantity(
                        line.quantity
                      )}
                      {" × "}
                      {formatCurrency(
                        line.priceExcludingTax
                      )}
                      {" HT"}
                    </span>
                  </div>

                  <strong>
                    {formatCurrency(
                      line.amountIncludingTax
                    )}
                  </strong>
                </div>
              )
            )}
      </div>


      <dl
        className="retail-pos-ticket__totals"
      >
        <div>
          <dt>Total HT</dt>
          <dd>
            {formatCurrency(
              presentation.totals
                .amountExcludingTax
            )}
          </dd>
        </div>

        <div>
          <dt>TVA</dt>
          <dd>
            {formatCurrency(
              presentation.totals
                .vatAmount
            )}
          </dd>
        </div>

        {presentation.totals
          .reduction > 0 && (
          <div>
            <dt>Remise</dt>
            <dd>
              {formatCurrency(
                presentation.totals
                  .reduction
              )}
            </dd>
          </div>
        )}

        <div
          className="retail-pos-ticket__total"
        >
          <dt>Total TTC</dt>
          <dd>
            {formatCurrency(
              presentation.totals
                .amountIncludingTax
            )}
          </dd>
        </div>
      </dl>


      {presentation.payments.length > 0 && (
        <div
          className="retail-pos-ticket__payments"
          data-testid="retail-pos-ticket-payments"
        >
          <h4>
            Paiements
          </h4>

          {presentation.payments.map(
            (payment, index) => (
              <div
                key={
                  payment.uuid
                  ?? `payment-${index}`
                }
              >
                <span>
                  {formatPaymentMethod(
                    payment.means_payment
                    ?? payment.payment_method
                  )}
                </span>

                <strong>
                  {formatCurrency(
                    payment.amount
                  )}
                </strong>
              </div>
            )
          )}
        </div>
      )}


      {outputError && (
        <p
          role="alert"
          className="retail-pos-ticket__error"
          data-testid="retail-pos-ticket-output-error"
        >
          {outputError}
        </p>
      )}


      <div
        className="retail-pos-ticket__actions"
      >
        <button
          type="button"
          onClick={
            handlePreparePdf
          }
          disabled={
            outputLoading
          }
          data-testid="retail-pos-ticket-pdf"
        >
          {outputLoading
            ? "Préparation…"
            : "Préparer le PDF"}
        </button>

        <button
          type="button"
          onClick={
            handleOpenForPrint
          }
          disabled={
            outputLoading
          }
          data-testid="retail-pos-ticket-print"
        >
          Ouvrir pour impression
        </button>

        {outputUrl && (
          <a
            href={
              outputUrl
            }
            target="_blank"
            rel="noreferrer"
            data-testid="retail-pos-ticket-pdf-link"
          >
            Ouvrir le PDF
          </a>
        )}
      </div>
    </section>
  );
}


function normalizePdfBlob(
  output
) {
  if (
    typeof Blob !== "undefined"
    && output instanceof Blob
  ) {
    return output;
  }

  if (
    typeof Blob !== "undefined"
    && output?.data instanceof Blob
  ) {
    return output.data;
  }

  throw new TypeError(
    "Receipt output must be a PDF Blob."
  );
}


function formatCurrency(
  value
) {
  const amount =
    Number(value ?? 0);

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style:
        "currency",

      currency:
        "EUR",
    }
  ).format(
    Number.isFinite(amount)
      ? amount
      : 0
  );
}


function formatQuantity(
  value
) {
  const quantity =
    Number(value ?? 0);

  return Number.isFinite(quantity)
    ? quantity
    : 0;
}


function formatDate(
  value
) {
  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleString(
    "fr-FR"
  );
}


function formatPaymentMethod(
  value
) {
  const normalized =
    String(
      value ?? ""
    ).trim();

  if (!normalized) {
    return "Paiement";
  }

  const labels = {
    card:
      "Carte",

    cash:
      "Espèces",

    check:
      "Chèque",

    voucher:
      "Bon / avoir",

    restaurant_ticket:
      "Titre restaurant",
  };

  return labels[normalized]
    ?? normalized;
}


RetailPOSTicketWorkspace.propTypes = {
  runtime:
    PropTypes.shape({
      printReceipt:
        PropTypes.func.isRequired,
    }).isRequired,

  context:
    PropTypes.shape({
      companyUuid:
        PropTypes.string,

      activityUuid:
        PropTypes.string,
    }).isRequired,

  receiptSnapshot:
    PropTypes.object,
};
